import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import R from 'ramda';
import React, { useMemo } from 'react';
import { Form, Field } from 'react-final-form';
import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import styled from 'styled-components';

import { OpeningTimes as OpeningTimesT, VenuePatchData } from 'apis/restaurant-api/merchant-admin/venue/types';
import {
  EMPTY_WEEKLY_SCHEDULE_INPUT_ENTRY,
  fromWeeklyScheduleInputEntriesToWeeklySchedule,
  fromWeeklyScheduleToWeeklyScheduleInputEntries,
  WeeklyScheduleInput,
  WeeklyScheduleInputEntry,
} from 'common/components/weekly-schedules';
import { Typography as T } from 'common/typography';

import { reportOpeningTimesEntryAdd, reportOpeningTimesEntryRemove } from '../behavioral';
import { isOpeningTimesEmpty } from '../utils';

type Props = {
  openingTimes: OpeningTimesT | null;
  showSpecialHoursWarning: boolean;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (data: VenuePatchData) => void;
};

type InnerProps = Props & InjectedIntlProps;

const Header = styled(T.Heading6)`
  color: ${cssThemedValue('textPrimary')};
  display: flex;
  justify-content: space-between;
  margin-block-end: 24px;
  flex-wrap: wrap;
  gap: 12px 8px;
`;

const FormButtons = styled.div`
  margin-inline-start: auto;
  margin-block: -6px; /** to not affect the header spacings */
`;

const CancelButton = styled(Button).attrs({ size: 'small', variant: 'lightBlue' })`
  margin-inline-end: 12px;
`;

const SaveButton = styled(Button).attrs({ size: 'small', variant: 'blue' })``;

const SpecialHoursAlert = styled(Alert).attrs({
  variant: 'warning',
})`
  margin-block-end: 24px;
`;

const OpeningTimesForm = ({
  intl,
  onCancel,
  onSubmit,
  showSpecialHoursWarning,
  submitting,
  openingTimes,
}: InnerProps) => {
  const t = (id: string) => intl.formatMessage({ id });

  const values = useMemo(
    () =>
      openingTimes === null || isOpeningTimesEmpty(openingTimes)
        ? [EMPTY_WEEKLY_SCHEDULE_INPUT_ENTRY]
        : fromWeeklyScheduleToWeeklyScheduleInputEntries(openingTimes),
    [openingTimes],
  );

  const handleSubmit = ({ entries }: { entries: WeeklyScheduleInputEntry[] }) => {
    onSubmit({
      opening_times: fromWeeklyScheduleInputEntriesToWeeklySchedule(entries),
    });
  };

  return (
    <Form onSubmit={handleSubmit} initialValues={{ entries: values }} initialValuesEqual={R.equals}>
      {({ handleSubmit: formSubmit, pristine }) => {
        return (
          <>
            <Header>
              {t('venue-details.opening-times')}
              <FormButtons>
                <CancelButton onClick={onCancel} disabled={submitting}>
                  {t('state.cancel')}
                </CancelButton>
                <SaveButton disabled={pristine || submitting} onClick={formSubmit}>
                  {t('state.save')}
                </SaveButton>
              </FormButtons>
            </Header>
            {showSpecialHoursWarning && (
              <SpecialHoursAlert title={t('venue-details.special-hours')}>
                {t('special-hours.erase-warning')}
              </SpecialHoursAlert>
            )}
            <Field<WeeklyScheduleInputEntry[]> name="entries" isEqual={R.equals}>
              {({ input }) => {
                return (
                  <WeeklyScheduleInput
                    onChange={input.onChange}
                    value={input.value}
                    pristine={pristine}
                    onAddEntry={() => {
                      reportOpeningTimesEntryAdd();
                    }}
                    onRemoveEntry={() => {
                      reportOpeningTimesEntryRemove();
                    }}
                  />
                );
              }}
            </Field>
          </>
        );
      }}
    </Form>
  );
};

export default injectIntl(OpeningTimesForm);
